import { AnimatePresence, motion } from "framer-motion"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { useEffect, useRef, type ReactNode } from "react"
import { useResizablePanelWidth } from "@/hooks/use-resizable-panel-width"
import { useAppMainMode } from "@/layouts/app-main-mode"
import { cn } from "@/lib/utils"

/** Default peek width — matches Notion-style side peek. */
export const SIDE_PEEK_DEFAULT_WIDTH = "min(32rem, 40vw)"

export const SIDE_PEEK_SURFACE_CLASS = "bg-ui-bg-elevated"

const OVERLAY_WIDTH_DEFAULT = 560
const OVERLAY_WIDTH_MIN = 440
const OVERLAY_WIDTH_MAX = 960
const OVERLAY_MAX_WIDTH_RATIO = 0.75

/**
 * Horizontal inset for filters / table / pagination.
 * In the table's horizontal scroll: leading gutter at scrollLeft === 0, trailing
 * at scrollLeft max — matches shared-toolbar `main` padding (`p-4`).
 */
export const SIDE_PEEK_CONTENT_INSET_X = "px-4"

type SidePeekLayoutProps = {
  children: ReactNode
  /** Panel chrome + body. Rendered only while `open`. */
  peek: ReactNode
  open: boolean
  panelWidth?: string
  className?: string
  mainClassName?: string
  /** Не занимать main scrollport: страница уже внутри flush-панели со своим скроллом. */
  contained?: boolean
}

/**
 * Table + right peek layout. Registers AppShell `main` as dual-axis scrollport
 * (@container/scrollport) so DataTable sticky headers and SidePeekFooter `100cqw` work
 * on any route without URL allowlists.
 * No horizontal page padding — table is full-bleed; left inset lives in the
 * scroll content (Notion: visible only when horizontal scroll is at 0).
 * Peek sticks to the top-right corner of the main scrollport on both axes and overlays
 * the content: it takes no width, so filters and the table never reflow when it opens.
 */
export function SidePeekLayout({
  children,
  peek,
  open,
  panelWidth = SIDE_PEEK_DEFAULT_WIDTH,
  className,
  mainClassName,
  contained = false,
}: SidePeekLayoutProps) {
  // Внутри flush-каркаса (биллинг компании) скролл принадлежит панели, а не main.
  useAppMainMode(contained ? "flush" : "scrollport")

  return (
    // `w-max min-w-full`: контейнер тянется на всю ширину контента (широкая таблица),
    // иначе sticky-панели некуда смещаться при горизонтальном скролле.
    // contained: колонка на высоту flush-панели, таблица скроллится внутри, без py-4.
    <div
      className={cn(contained ? "flex h-full min-h-0 w-full min-w-0" : "flex w-max min-w-full items-start", className)}
    >
      <div
        className={cn("min-w-0 flex-1", contained ? "flex min-h-0 flex-col overflow-hidden" : "py-4", mainClassName)}
      >
        {children}
      </div>

      <AnimatePresence initial={false}>
        {open ? (
          // Отрицательный отступ на всю ширину: панель не занимает места в строке и ложится поверх.
          // overflow-hidden не даёт выезжающей панели расширить горизонтальный скролл.
          <aside
            key="side-peek"
            className="pointer-events-none sticky top-0 right-0 z-20 h-[calc(100dvh-3rem)] shrink-0 self-start overflow-hidden"
            style={{ width: panelWidth, marginLeft: `calc(-1 * ${panelWidth})` }}
          >
            <SidePeekSurface>{peek}</SidePeekSurface>
          </aside>
        ) : null}
      </AnimatePresence>
    </div>
  )
}

function SidePeekSurface({ children, edge }: { children: ReactNode; edge?: ReactNode }) {
  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", bounce: 0, duration: 0.28 }}
      className={cn(
        "pointer-events-auto relative flex h-full min-h-0 flex-col border-l border-ui-border",
        SIDE_PEEK_SURFACE_CLASS,
      )}
    >
      {edge}
      {children}
    </motion.div>
  )
}

/**
 * Peek over a full-height layout that scrolls itself (task board): the panel is pinned to the
 * right edge of the nearest `relative` ancestor and overlays it, same look and motion as SidePeekLayout.
 * The left edge drags to resize, double click restores the default; the width is remembered under `storageKey`.
 */
export function SidePeekOverlay({
  open,
  peek,
  storageKey,
  onOutsideClick,
}: {
  open: boolean
  peek: ReactNode
  storageKey: string
  /**
   * Клик по перекрытой области мимо панели (лента, колонки под ней). Меню и окна в порталах вне области
   * панель не закрывают.
   */
  onOutsideClick?: () => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const asideRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!open || !onOutsideClick) return
    const onPointerDown = (event: PointerEvent) => {
      const area = containerRef.current?.parentElement
      const target = event.target
      if (!area || !(target instanceof Node) || !area.contains(target)) return
      if (asideRef.current?.contains(target)) return
      onOutsideClick()
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [open, onOutsideClick])
  const { width, minWidth, maxWidth, onResizePointerDown, resetWidth } = useResizablePanelWidth({
    storageKey,
    defaultWidth: OVERLAY_WIDTH_DEFAULT,
    minWidth: OVERLAY_WIDTH_MIN,
    maxWidth: OVERLAY_WIDTH_MAX,
    maxWidthRatio: OVERLAY_MAX_WIDTH_RATIO,
    containerRef,
  })

  return (
    <>
      <div ref={containerRef} aria-hidden className="pointer-events-none absolute inset-0" />
      <AnimatePresence initial={false}>
        {open ? (
          <aside
            ref={asideRef}
            key="side-peek"
            className="pointer-events-none absolute inset-y-0 right-0 z-20 overflow-hidden"
            style={{ width: `min(${width}px, 100%)` }}
          >
            <SidePeekSurface
              edge={
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-valuenow={Math.round(width)}
                  aria-valuemin={minWidth}
                  aria-valuemax={Math.round(maxWidth)}
                  onPointerDown={onResizePointerDown}
                  onDoubleClick={resetWidth}
                  className="group/resize absolute inset-y-0 -left-px z-10 w-2 cursor-ew-resize touch-none"
                >
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-px transition-colors group-hover/resize:bg-ui-text-muted group-active/resize:bg-ui-text-muted"
                  />
                </div>
              }
            >
              {peek}
            </SidePeekSurface>
          </aside>
        ) : null}
      </AnimatePresence>
    </>
  )
}

/**
 * Row that must stay inside the visible scrollport width (pagination and such):
 * `100cqw` is the width of the `@container/scrollport` main, not of the wide table.
 */
export function SidePeekFooter({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("sticky left-0 w-[100cqw]", className)}>{children}</div>
}

export const SIDE_PEEK_ICON_BUTTON_CLASS =
  "rounded-lg p-1.5 text-ui-text-muted transition-colors hover:bg-ui-surface-raised hover:text-ui-text-primary"

/** Поле ввода в фокусе — стрелки и буквенные клавиши панели не перехватываем. */
export function isTypingTarget(target: EventTarget | null) {
  return target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
}

/** ← / → — соседний элемент списка, открытого в панели (очередь проверки, задачи «Ждёт вас»). */
export function useSidePeekArrowKeys(onPrev: (() => void) | null, onNext: (() => void) | null) {
  useEffect(() => {
    if (!onPrev && !onNext) return
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return
      if (e.key === "ArrowRight" && onNext) onNext()
      else if (e.key === "ArrowLeft" && onPrev) onPrev()
      else return
      e.preventDefault()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onPrev, onNext])
}

/** Стрелки «предыдущий / следующий» в шапке панели, открытой из списка. */
export function SidePeekPager({
  prevLabel,
  nextLabel,
  onPrev,
  onNext,
}: {
  prevLabel: string
  nextLabel: string
  onPrev: (() => void) | null
  onNext: (() => void) | null
}) {
  return (
    <>
      <button
        type="button"
        className={SIDE_PEEK_ICON_BUTTON_CLASS}
        aria-label={prevLabel}
        disabled={!onPrev}
        onClick={() => onPrev?.()}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={SIDE_PEEK_ICON_BUTTON_CLASS}
        aria-label={nextLabel}
        disabled={!onNext}
        onClick={() => onNext?.()}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </>
  )
}

export function SidePeekSection({
  title,
  action,
  children,
  className,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn("space-y-2.5", className)}>
      <div className="flex min-h-7 items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

type SidePeekPanelProps = {
  title: ReactNode
  description?: ReactNode
  leading?: ReactNode
  actions?: ReactNode
  closeLabel: string
  onClose: () => void
  children: ReactNode
  className?: string
  bodyClassName?: string
  /** Close on Escape while mounted. Default true. */
  closeOnEscape?: boolean
  /**
   * Шапка в высоту шапки колонки ассистента (AGENT_COLUMN_HEADER_CLASS): для панелей, которые стоят рядом с ней
   * поверх ленты и доски задач. Описание в этом режиме идёт в строку с заголовком.
   */
  compactHeader?: boolean
}

/**
 * Peek chrome: header + independently scrollable body.
 * Use inside `SidePeekLayout` `peek` slot.
 */
export function SidePeekPanel({
  title,
  description,
  leading,
  actions,
  closeLabel,
  onClose,
  children,
  className,
  bodyClassName,
  closeOnEscape = true,
  compactHeader = false,
}: SidePeekPanelProps) {
  useEffect(() => {
    if (!closeOnEscape) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) onClose()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [closeOnEscape, onClose])

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <div
        className={cn(
          "flex shrink-0 justify-between gap-3 border-b border-ui-border px-5",
          // 37px — высота AgentColumnHeader: строка text-xs, py-2.5 и линия.
          compactHeader ? "h-[37px] items-center" : "items-start py-4",
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          {leading != null ? <div className="shrink-0">{leading}</div> : null}
          <div className={cn("min-w-0", compactHeader && "flex items-baseline gap-2")}>
            <h2 className={cn("font-semibold text-ui-text-primary", compactHeader ? "truncate text-sm" : "text-base")}>
              {title}
            </h2>
            {description != null ? (
              <div className={cn("truncate font-mono text-xs text-ui-text-muted", !compactHeader && "mt-1")}>
                {description}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {actions}
          <button type="button" className={SIDE_PEEK_ICON_BUTTON_CLASS} aria-label={closeLabel} onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className={cn("it2tv-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4", bodyClassName)}>
        {children}
      </div>
    </div>
  )
}
